import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGlossary } from '../context/GlossaryContext.jsx'
import Seo from '../components/Seo.jsx'
import LastUpdated from '../components/LastUpdated.jsx'

export default function Glossary() {
  const { glossary } = useGlossary()
  const [q, setQ] = useState('')

  // DefinedTermSet structured data — schema.org type for a glossary. Helps
  // search engines and AI models pull clean, attributed definitions.
  const termSetLd = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: 'Peptide Glossary',
    url: 'https://calculatemypeptides.com/glossary',
    hasDefinedTerm: glossary.map((g) => ({ '@type': 'DefinedTerm', name: g.term, description: g.def })),
  }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return glossary
    return glossary.filter((g) => g.term.toLowerCase().includes(s) || g.aka?.toLowerCase().includes(s) || g.def.toLowerCase().includes(s))
  }, [q, glossary])

  // First letter of each term, for the A–Z jump bar.
  const letters = [...new Set(glossary.map((g) => g.term[0].toUpperCase()))]

  return (
    <main className="page">
      <Seo
        title="Peptide Glossary — Dosing Terms | CalculateMyPeptides"
        description="Plain-language definitions of peptide reconstitution and dosing terms — bacteriostatic water, U-100 units, mcg vs mg, half-life, titration, GHRP, GLP-1, and more."
        image="/og/glossary.png"
        jsonLd={termSetLd}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Glossary', path: '/glossary' }]}
      />
      <div className="container page-narrow">
        <span className="pill blue" style={{ marginBottom: 10 }}>Reference</span>
        <h1 className="section-title">Peptide Glossary</h1>
        <p className="section-sub" style={{ maxWidth: '62ch' }}>
          Plain-language definitions of the terms you’ll run into when reconstituting and dosing peptides — from
          bacteriostatic water and U-100 units to half-life, titration, and blends. Pairs with our{' '}
          <Link to="/calculator">peptide calculator</Link> and <Link to="/peptides">education library</Link>.
        </p>
        <LastUpdated />

        <div className="lib-search" style={{ margin: '18px 0 10px' }}>
          <span className="lib-search-icon">🔍</span>
          <input className="input" placeholder="Search terms…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>

        {!q && (
          <div className="glossary-az">
            {letters.map((l) => <a key={l} href={`#${l}`} className="glossary-az-link">{l}</a>)}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="card empty"><p style={{ margin: 0 }}>No terms match “{q}”.</p></div>
        ) : (
          <dl className="glossary">
            {filtered.map((g, i) => {
              const letter = g.term[0].toUpperCase()
              const firstOfLetter = !q && filtered.findIndex((x) => x.term[0].toUpperCase() === letter) === i
              return (
                <div className="glossary-item" key={g.term} id={firstOfLetter ? letter : undefined}>
                  <dt id={g.slug}>
                    <Link to={`/glossary/${g.slug}`} className="glossary-term-link">{g.term}</Link>
                    {g.aka && <span className="glossary-aka"> · also “{g.aka}”</span>}
                  </dt>
                  <dd>{g.def}</dd>
                </div>
              )
            })}
          </dl>
        )}

        <p className="muted small" style={{ marginTop: 24, maxWidth: '68ch' }}>
          Educational reference only — not medical advice. Always follow the guidance of a qualified healthcare
          professional.
        </p>
      </div>
    </main>
  )
}
